import { ChangeDetectionStrategy, Component, computed, input, InputSignal, Signal } from '@angular/core';
import { SzAccuracySession } from './sz-accuracy-stack.types';

interface SegmentRender {
  readonly y: number;
  readonly height: number;
  readonly kind: 'a' | 'c' | 'd' | 'm' | 'ns';
}

interface BarRender {
  readonly x: number;
  readonly width: number;
  readonly label: string;
  readonly isLatest: boolean;
  readonly segments: readonly SegmentRender[];
}

interface ScaledChart {
  readonly bars: readonly BarRender[];
  readonly yLabels: readonly { y: number; text: string }[];
}

const VIEW_W: number = 360;
const VIEW_H: number = 220;
const PAD_L: number = 30;
const PAD_R: number = 16;
const PAD_T: number = 18;
const PAD_B: number = 32;
const MAX_BAR_WIDTH: number = 22;

@Component({
  selector: 'sz-accuracy-stack',
  templateUrl: './sz-accuracy-stack.component.html',
  styleUrls: ['./sz-accuracy-stack.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class SzAccuracyStackComponent {
  readonly sessions: InputSignal<readonly SzAccuracySession[]> = input.required<readonly SzAccuracySession[]>();

  readonly viewBox: string = `0 0 ${VIEW_W} ${VIEW_H}`;
  readonly viewW: number = VIEW_W;
  readonly viewH: number = VIEW_H;
  readonly padL: number = PAD_L;

  readonly legend: ReadonlyArray<{ kind: SegmentRender['kind']; label: string }> = [
    { kind: 'a', label: 'A' },
    { kind: 'c', label: 'C' },
    { kind: 'd', label: 'D' },
    { kind: 'm', label: 'Miss' },
    { kind: 'ns', label: 'NS' }
  ];

  readonly chart: Signal<ScaledChart | null> = computed((): ScaledChart | null => {
    const sessions: readonly SzAccuracySession[] = this.sessions();
    if (sessions.length === 0) return null;

    const plotW: number = VIEW_W - PAD_L - PAD_R;
    const plotH: number = VIEW_H - PAD_T - PAD_B;
    const slotW: number = plotW / sessions.length;
    const barWidth: number = Math.min(MAX_BAR_WIDTH, slotW * 0.7);

    const bars: BarRender[] = sessions.map((s: SzAccuracySession, i: number): BarRender => {
      const cx: number = PAD_L + slotW * (i + 0.5);
      const x: number = cx - barWidth / 2;

      // Stack from bottom (PAD_T + plotH) upward: A (best) → C → D → M → NS (worst).
      const segments: SegmentRender[] = [];
      let cursor: number = PAD_T + plotH;
      const stack: readonly { kind: SegmentRender['kind']; pct: number }[] = [
        { kind: 'a', pct: s.aPct },
        { kind: 'c', pct: s.cPct },
        { kind: 'd', pct: s.dPct },
        { kind: 'm', pct: s.mPct },
        { kind: 'ns', pct: s.nsPct }
      ];
      for (const seg of stack) {
        if (seg.pct <= 0) continue;
        const h: number = (seg.pct / 100) * plotH;
        cursor -= h;
        segments.push({ y: cursor, height: h, kind: seg.kind });
      }
      return {
        x,
        width: barWidth,
        label: s.label,
        isLatest: i === sessions.length - 1,
        segments
      };
    });

    return {
      bars,
      yLabels: [
        { y: PAD_T, text: '100%' },
        { y: PAD_T + plotH * 0.5, text: '50%' },
        { y: PAD_T + plotH, text: '0%' }
      ]
    };
  });
}
